import { useCallback } from "react";
import { useLocation } from "react-router-dom-v5-compat";
import {
  SupportHubRawEventName,
  type SupportHubCommonProperties,
  type SupportHubDestination,
  type SupportHubSource,
  type SupportHubView,
  useEmitCoreSelfServeRawEvent,
} from "@b2bportal/core-self-serve";
import { pathnameToEntryPointWithFallback } from "@/components/HtsAssistChatWidget/htsAssistEntryPoint";
import { pathnameToSupportContext } from "@/components/HtsAssistChatWidget/htsAssistSupportContext";

/**
 * Properties every support hub event carries, read from the current route:
 * `entry_point` from `pathnameToEntryPointWithFallback` (known surfaces get a
 * named slug like "home" or "flights-trip-details"; unmatched routes fall back
 * to the raw pathname), plus `product` and `reservation_id` when the route is
 * a booking's trip-details page.
 */
export const getSupportHubProperties = (
  pathname: string
): SupportHubCommonProperties => {
  const supportContext = pathnameToSupportContext(pathname);
  return {
    entry_point: pathnameToEntryPointWithFallback(pathname),
    product: supportContext?.productType,
    reservation_id: supportContext?.bookingId,
  };
};

/**
 * Dispatches the support hub modal events. Each callback adds the shared route
 * properties, so call sites only pass what is specific to the event.
 */
export const useSupportHubTracking = () => {
  const { pathname } = useLocation();

  const emitViewed = useEmitCoreSelfServeRawEvent(
    SupportHubRawEventName.SupportHubViewed
  );
  const emitTappedManageBooking = useEmitCoreSelfServeRawEvent(
    SupportHubRawEventName.SupportHubTappedManageBooking
  );
  const emitTappedFaq = useEmitCoreSelfServeRawEvent(
    SupportHubRawEventName.SupportHubTappedFaq
  );
  const emitTappedContactSupport = useEmitCoreSelfServeRawEvent(
    SupportHubRawEventName.SupportHubTappedContactSupport
  );
  const emitDismissed = useEmitCoreSelfServeRawEvent(
    SupportHubRawEventName.SupportHubDismissed
  );

  return {
    trackViewed: useCallback(
      (source?: SupportHubSource) =>
        emitViewed({
          properties: { ...getSupportHubProperties(pathname), source },
        }),
      [emitViewed, pathname]
    ),
    trackTappedManageBooking: useCallback(
      () =>
        emitTappedManageBooking({
          properties: getSupportHubProperties(pathname),
        }),
      [emitTappedManageBooking, pathname]
    ),
    trackTappedFaq: useCallback(
      () => emitTappedFaq({ properties: getSupportHubProperties(pathname) }),
      [emitTappedFaq, pathname]
    ),
    trackTappedContactSupport: useCallback(
      () =>
        emitTappedContactSupport({
          properties: getSupportHubProperties(pathname),
        }),
      [emitTappedContactSupport, pathname]
    ),
    trackDismissed: useCallback(
      (lastView: SupportHubView) =>
        emitDismissed({
          properties: {
            ...getSupportHubProperties(pathname),
            last_view: lastView,
          },
        }),
      [emitDismissed, pathname]
    ),
  };
};

/** Dispatches `support_hub_help_launched` for the floating help button. */
export const useTrackSupportHubHelpLaunched = () => {
  const { pathname } = useLocation();
  const emitHelpLaunched = useEmitCoreSelfServeRawEvent(
    SupportHubRawEventName.SupportHubHelpLaunched
  );

  return useCallback(
    (destination: SupportHubDestination) =>
      emitHelpLaunched({
        properties: { ...getSupportHubProperties(pathname), destination },
      }),
    [emitHelpLaunched, pathname]
  );
};
